/** Quick-peek: a right-hand panel for the focused row, beside the table rather than over it.
 *
 * Space on a row (or its peek icon) opens it; Up and Down move to the next row and **the panel
 * follows** (the grid moves the key, this draws it); Escape closes it and the grid puts focus back on
 * the row; Enter opens the row's full page. The body is the **page's slot** (`renderPeek`): a receipt
 * summary, the figure, its evidence - this component knows none of it and adds no figure of its own, so
 * rule 3 is the page's to keep.
 *
 * It is a non-modal region, not a dialog: the table stays usable while it is open, which is what makes
 * "Down, Down, Down" through a list of figures possible. It is portalled to the body so the shell's
 * `overflow: hidden` cannot clip it.
 */

import { ArrowSquareOut, X } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import styles from "./tablePower.module.css";

export function PeekPanel({
  title,
  onClose,
  onOpen,
  children,
}: {
  title: string;
  onClose: () => void;
  /** Open the row's full page. Absent: no button (the row opens nothing). */
  onOpen?: () => void;
  children: ReactNode;
}) {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        closeRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return createPortal(
    <aside className={styles.peek} aria-label="Row preview" data-testid="peek-panel">
      <header className={styles.peekHead}>
        <h2 className={styles.peekTitle}>{title}</h2>
        {onOpen ? (
          <button type="button" className={styles.tool} onClick={onOpen} data-testid="peek-open">
            <ArrowSquareOut size={14} aria-hidden="true" />
            Open
          </button>
        ) : null}
        <button type="button" className={styles.iconTool} onClick={onClose} aria-label="Close preview" data-testid="peek-close">
          <X size={15} aria-hidden="true" />
        </button>
      </header>
      <div className={styles.peekBody}>{children}</div>
    </aside>,
    document.body,
  );
}
